import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, getDocs, doc, getDoc, updateDoc, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';

import ListingCard from '../components/ui/ListingCard';
import MembershipBadge from '../components/shared/MembershipBadge';
import Checklist from '../features/planner/Checklist';
import BudgetTracker from '../features/planner/BudgetTracker';
import WeddingDashboard from './WeddingDashboard';

// Icon components (SVG icons)
const PlannerIcons = {
    Grid: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h4a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h4a2 2 0 012 2v4a2 2 0 01-2 2h-4a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h4a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4zm10 0a2 2 0 012-2h4a2 2 0 012 2v4a2 2 0 01-2 2h-4a2 2 0 01-2-2v-4z" /></svg>,
    Globe: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20H7m6-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    Check: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    Dollar: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    Mail: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
    Box: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m0 0L4 7m8 4v10l8-4v-10L12 11z" /></svg>,
    Star: () => <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>,
};

export default function PlannerDashboard({ user, navigateTo, db }) {
    const [myRentals, setMyRentals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [bookingToReview, setBookingToReview] = useState(null);
    const [showDeliveryConfirmModal, setShowDeliveryConfirmModal] = useState(false);
    const [bookingToConfirm, setBookingToConfirm] = useState(null);
    const [favorites, setFavorites] = useState([]);
    const [vendors, setVendors] = useState({});
    const [evites, setEvites] = useState([]); 
    const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('hostit_planner_tab') || 'events');

    // NEW: Events State
    const [myEvents, setMyEvents] = useState([]);
    const [viewingEvent, setViewingEvent] = useState(null);
    const [showCreateEventModal, setShowCreateEventModal] = useState(false);
    const [newEventData, setNewEventData] = useState({ title: '', date: '', location: '', type: 'Birthday' });
    const [myPlannerProjects, setMyPlannerProjects] = useState([]);

    // Session storage persistence
    useEffect(() => sessionStorage.setItem('hostit_planner_tab', activeTab), [activeTab]);

    const sortedRentals = useMemo(() => {
        return [...myRentals].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    }, [myRentals]);

    const getStatusClasses = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
            case 'awaitingPayment':
            case 'awaitingFinalPayment':
            case 'awaitingPlannerConfirmation': return 'bg-orange-50 text-orange-700 border-orange-200';
            case 'deposit-paid': return 'bg-teal-50 text-teal-700 border-teal-200';
            case 'paid': return 'bg-green-50 text-green-700 border-green-200';
            case 'booked': return 'bg-green-50 text-green-700 border-green-200'; 
            case 'delivered': return 'bg-blue-50 text-blue-700 border-blue-200';
            case 'awaitingPickup': return 'bg-purple-50 text-purple-700 border-purple-200';
            case 'returned': return 'bg-gray-100 text-gray-600 border-gray-200';
            case 'cancelled':
            case 'declined': return 'bg-red-50 text-red-700 border-red-200';
            default: return 'bg-gray-50 text-gray-600 border-gray-200';
        }
    };

    useEffect(() => {
        if (!user || user.isAnonymous) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const qRentals = query(collection(db, "bookings"), where("renterId", "==", user.uid));
        const unsubRentals = onSnapshot(qRentals, (snapshot) => {
            setMyRentals(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (error) => {
            if (error.code !== 'permission-denied') console.error("Rentals listener error:", error);
        });

        // NEW: Fetch Events
        const qEvents = query(collection(db, `users/${user.uid}/events`), orderBy("createdAt", "desc"));
        const unsubEvents = onSnapshot(qEvents, (snapshot) => {
            const fetchedEvents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedEvents.sort((a, b) => {
                if (!a.date) return 1;
                if (!b.date) return -1;
                return a.date.localeCompare(b.date);
            });
            setMyEvents(fetchedEvents);
        }, (error) => {
            if (error.code !== 'permission-denied') console.error("Events listener error:", error);
        });

        // NEW: Fetch Planner Pro Projects
        const qPlanner = query(collection(db, "plannerProjects"), where("plannerId", "==", user.uid));
        const unsubPlanner = onSnapshot(qPlanner, (snapshot) => {
            setMyPlannerProjects(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        // Fetch E-vites
        const qEvites = query(collection(db, `users/${user.uid}/evites`), orderBy("createdAt", "desc"));
        const unsubEvites = onSnapshot(qEvites, (snapshot) => {
            setEvites(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (error) => {
            if (error.code !== 'permission-denied') console.error("Evites listener error:", error);
        });

        const fetchFavorites = async () => {
            if (user.favorites && user.favorites.length > 0) {
                const listingsRef = collection(db, "listings");
                const qFavorites = query(listingsRef, where("__name__", "in", user.favorites.slice(0, 10)));
                const favSnapshot = await getDocs(qFavorites);
                const favListings = favSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setFavorites(favListings);

                const vendorIds = [...new Set(favListings.map(l => l.vendorId))];
                const newVendors = { ...vendors };
                for (const id of vendorIds) {
                    if (!newVendors[id]) {
                        const vendorSnap = await getDoc(doc(db, "users", id));
                        if (vendorSnap.exists()) {
                            newVendors[id] = vendorSnap.data();
                        }
                    }
                }
                setVendors(newVendors);
            } else {
                setFavorites([]);
            }
        };

        // Listen for user changes (favorites update)
        const unsubUser = onSnapshot(doc(db, "users", user.uid), (doc) => {
            fetchFavorites();
        }, (error) => {
            if (error.code !== 'permission-denied') console.error("User listener error:", error);
        });

        return () => {
            unsubRentals();
            unsubUser();
            unsubEvites();
            unsubEvents();
            unsubPlanner();
        };
    }, [user, db]);

    const handleUpdateBooking = async (bookingId, status) => {
        const bookingRef = doc(db, "bookings", bookingId);
        await updateDoc(bookingRef, { status: status });
    };

    const handleOpenReviewModal = (booking) => {
        setBookingToReview(booking);
        setShowReviewModal(true);
    };

    const handleOpenDeliveryConfirm = (booking) => {
        setBookingToConfirm(booking);
        setShowDeliveryConfirmModal(true);
    };

    const canLeaveReview = (rental) => {
        if (rental.status !== 'returned' || rental.reviewLeft) {
            return false;
        }
        const today = new Date();
        const returnDate = new Date(rental.returnDate);
        const diffTime = today - returnDate;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        return diffDays > 0 && diffDays <= 14;
    };

    // NEW: Handle Create Event
    const handleCreateEvent = async (e) => {
        e.preventDefault();
        if(!newEventData.title) return;
        await addDoc(collection(db, `users/${user.uid}/events`), {
            ...newEventData,
            createdAt: serverTimestamp()
        });
        setShowCreateEventModal(false);
        setNewEventData({ title: '', date: '', location: '', type: 'Birthday' });
    };

    // NEW: Handle linking a booking to an event directly from the dashboard
    const handleLinkToEvent = async (bookingId, eventId) => {
        try {
            await updateDoc(doc(db, "bookings", bookingId), { eventId: eventId || null });
        } catch (err) {
            console.error("Failed to link event:", err);
        }
    };

    // NEW: Handle linking a booking to a Planner Pro project
    const handleLinkToPlannerProject = async (booking, projectId) => {
        try {
            await updateDoc(doc(db, "bookings", booking.id), { plannerProjectId: projectId || null });
            
            if (projectId) {
                const vendorSnap = await getDoc(doc(db, "users", booking.vendorId));
                const vendorData = vendorSnap.exists() ? vendorSnap.data() : {};
                const listingSnap = await getDoc(doc(db, "listings", booking.listingId));
                const listingCategory = listingSnap.exists() ? listingSnap.data().category : 'Rentals';
                
                const projectRef = doc(db, "plannerProjects", projectId);
                const projectSnap = await getDoc(projectRef);
                
                if (projectSnap.exists()) {
                    const projectData = projectSnap.data();
                    const existingVendors = projectData.vendors || [];
                    
                    if (!existingVendors.some(v => v.id === booking.vendorId)) {
                        const newVendor = {
                            id: booking.vendorId,
                            name: vendorData.vendorName || vendorData.email || 'Unknown Vendor',
                            category: listingCategory,
                            status: 'Booked via Host-it!',
                            email: vendorData.email || '',
                            phone: vendorData.businessInfo?.phone || vendorData.phone || '',
                            cost: booking.totalCost || 0,
                            paid: (booking.payments || []).reduce((acc, p) => acc + p.amount, 0) || (booking.status === 'paid' ? booking.totalCost : 0),
                            contractSigned: booking.contractStatus === 'signed'
                        };
                        await updateDoc(projectRef, { vendors: [...existingVendors, newVendor] });
                    }
                }
            }
        } catch (err) {
            console.error("Failed to link project:", err);
        }
    };

    if (loading) return (
        <div className="flex justify-center items-center h-screen bg-gray-50">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
        </div>
    );

    return (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-screen">
            {showReviewModal && <div className="text-center">Review modal placeholder</div>}
            {showDeliveryConfirmModal && <div className="text-center">Delivery confirm modal placeholder</div>}
            
            {/* Create Event Modal */}
            {showCreateEventModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white p-6 rounded-xl shadow-xl max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4">Create New Event</h3>
                        <form onSubmit={handleCreateEvent} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Event Title</label>
                                <input type="text" placeholder="e.g. John's 30th" value={newEventData.title} onChange={e => setNewEventData({...newEventData, title: e.target.value})} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-pink-500 outline-none" required />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date</label>
                                    <input type="date" value={newEventData.date} onChange={e => setNewEventData({...newEventData, date: e.target.value})} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-pink-500 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Event Type</label>
                                    <select value={newEventData.type} onChange={e => setNewEventData({...newEventData, type: e.target.value})} className="w-full p-2 border rounded-md bg-white focus:ring-2 focus:ring-pink-500 outline-none">
                                        <option>Birthday</option>
                                        <option>Wedding</option>
                                        <option>Corporate</option>
                                        <option>Holiday</option>
                                        <option>Other</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Location (Optional)</label>
                                <input type="text" placeholder="e.g. Malibu Beach" value={newEventData.location} onChange={e => setNewEventData({...newEventData, location: e.target.value})} className="w-full p-2 border rounded-md focus:ring-2 focus:ring-pink-500 outline-none" />
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={() => setShowCreateEventModal(false)} className="px-4 py-2 bg-gray-200 rounded-lg font-semibold">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-pink-600 text-white rounded-lg font-semibold">Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="mb-8">
                <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">Welcome, {user.plannerName || user.firstName || 'Planner'}!</h2>
                    <MembershipBadge user={user} className="text-xs px-2 py-1 shadow-sm hidden sm:inline-flex" />
                </div>
                <p className="mt-2 text-gray-600">Here's what's happening in your Party Hub.</p>
            </div>
            
            {/* Pill-based Grouped Tabs */}
            <div className="overflow-x-auto custom-scrollbar no-scrollbar-mobile pb-4 mb-4 mt-2">
                <div className="flex gap-6 items-end min-w-max">
                    {[
                        {
                            group: 'Events & Websites',
                            tabs: [
                                { id: 'events', label: 'Party Hub', icon: PlannerIcons.Grid },
                                { id: 'weddings', label: 'Wedding Sites', icon: PlannerIcons.Globe }
                            ]
                        },
                        {
                            group: 'Planning Tools',
                            tabs: [
                                { id: 'checklist', label: 'Checklist', icon: PlannerIcons.Check },
                                { id: 'budget', label: 'Budget', icon: PlannerIcons.Dollar },
                                { id: 'evites', label: 'E-vites', icon: PlannerIcons.Mail }
                            ]
                        },
                        {
                            group: 'Marketplace',
                            tabs: [
                                { id: 'rentals', label: 'My Rentals', icon: PlannerIcons.Box },
                                { id: 'favorites', label: 'Favorites', icon: PlannerIcons.Star }
                            ]
                        }
                    ].map((category, idx) => (
                        <div key={idx} className="flex flex-col gap-2 flex-shrink-0">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">{category.group}</span>
                            <div className="flex bg-white rounded-xl shadow-sm border border-gray-200 p-1 gap-1">
                                {category.tabs.map(tab => {
                                    const IconComponent = tab.icon;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 font-bold text-[10px] sm:text-sm rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${activeTab === tab.id ? 'bg-pink-50 text-pink-700 shadow-sm ring-1 ring-pink-200' : 'bg-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                                        >
                                            <span className={`${activeTab === tab.id ? 'text-pink-600' : 'text-gray-400'}`}>
                                                <IconComponent />
                                            </span> 
                                            <span>{tab.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="animate-fade-in">
                {activeTab === 'events' && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                             <div>
                                <h3 className="text-lg font-bold text-gray-900">Party Hub</h3>
                                <p className="text-sm text-gray-500">Manage separate budgets, checklists, and rentals for each party.</p>
                             </div>
                             <button onClick={() => setShowCreateEventModal(true)} className="bg-pink-600 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-pink-700 shadow-md flex items-center gap-2 transition">
                                 <span>+</span> Create Event
                             </button>
                        </div>
                        
                        {myEvents.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {myEvents.map(event => (
                                    <div key={event.id} onClick={() => setViewingEvent(event)} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg hover:border-pink-200 transition cursor-pointer group relative overflow-hidden">
                                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pink-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className="text-xl font-bold text-gray-900 group-hover:text-pink-600 transition">{event.title}</h3>
                                            <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">{event.type}</span>
                                        </div>
                                        <p className="text-gray-500 text-sm mb-4 flex items-center gap-2">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                            {event.date ? new Date(event.date).toLocaleDateString() : 'No Date Set'}
                                        </p>
                                        <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
                                            <span className="text-sm font-semibold text-blue-600 group-hover:underline">Open Dashboard &rarr;</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
                                <div className="w-16 h-16 bg-purple-50 text-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">🎉</div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">No events created</h3>
                                <p className="text-gray-500 mb-6 max-w-sm mx-auto">Create an event to organize your budget, checklist, and rentals in one place.</p>
                                <button onClick={() => setShowCreateEventModal(true)} className="bg-purple-600 text-white px-6 py-2.5 rounded-full font-semibold hover:bg-purple-700 transition shadow-md shadow-purple-200">
                                    Create Event
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'rentals' && (
                    <div className="space-y-6">
                        {sortedRentals.length > 0 ? (
                            <div className="grid grid-cols-1 gap-6"> 
                                {sortedRentals.map(rental => (
                                    <div key={rental.id} onClick={() => navigateTo('bookingDetail', { booking: rental, from: 'plannerDashboard' })} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 cursor-pointer hover:shadow-md transition-all duration-200 group relative overflow-hidden">
                                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-pink-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-pink-600 transition-colors">{rental.listingTitle}</h3>
                                                    <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full font-medium">x{rental.quantity}</span>
                                                </div>
                                                <p className="text-sm text-gray-500 mb-2">Order #{rental.bookingNumber || rental.id.slice(0,8).toUpperCase()}</p>
                                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                                    <span className="flex items-center gap-1">
                                                        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                                        {new Date(rental.date).toLocaleDateString()} - {new Date(rental.returnDate).toLocaleDateString()}
                                                    </span>
                                                </div>

                                                {/* Quick Link to Event/Project */}
                                                <div className="mt-3 flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                    {rental.eventId ? (
                                                        <div className="flex items-center gap-2">
                                                            <span className="bg-purple-100 text-purple-800 text-xs font-bold px-2.5 py-1 rounded-md flex items-center gap-1 border border-purple-200">
                                                                🎉 {myEvents.find(e => e.id === rental.eventId)?.title || 'Linked Event'}
                                                            </span>
                                                            <button onClick={() => handleLinkToEvent(rental.id, '')} className="text-gray-400 hover:text-red-500 text-[10px] uppercase font-bold tracking-wide transition">Unlink</button>
                                                        </div>
                                                    ) : (
                                                        myEvents.length > 0 && (
                                                            <select 
                                                                value="" 
                                                                onChange={(e) => handleLinkToEvent(rental.id, e.target.value)}
                                                                className="text-xs bg-gray-50 border border-gray-200 text-gray-600 rounded-md px-2 py-1 outline-none focus:border-purple-400 hover:bg-white transition cursor-pointer"
                                                            >
                                                                <option value="" disabled>+ Link to Event...</option>
                                                                {myEvents.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
                                                            </select>
                                                        )
                                                    )}

                                                    {rental.plannerProjectId ? (
                                                        <div className="flex items-center gap-2">
                                                            <span className="bg-pink-100 text-pink-800 text-xs font-bold px-2.5 py-1 rounded-md flex items-center gap-1 border border-pink-200">
                                                                ✨ {myPlannerProjects.find(p => p.id === rental.plannerProjectId)?.clientNames || 'Linked Project'}
                                                            </span>
                                                            <button onClick={() => handleLinkToPlannerProject(rental, '')} className="text-gray-400 hover:text-red-500 text-[10px] uppercase font-bold tracking-wide transition">Unlink</button>
                                                        </div>
                                                    ) : (
                                                        myPlannerProjects.length > 0 && (
                                                            <select 
                                                                value="" 
                                                                onChange={(e) => handleLinkToPlannerProject(rental, e.target.value)}
                                                                className="text-xs bg-gray-50 border border-gray-200 text-gray-600 rounded-md px-2 py-1 outline-none focus:border-pink-400 hover:bg-white transition cursor-pointer"
                                                            >
                                                                <option value="" disabled>+ Link to Planner Pro...</option>
                                                                {myPlannerProjects.map(p => <option key={p.id} value={p.id}>{p.clientNames}</option>)}
                                                            </select>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="flex flex-wrap items-center gap-3 md:justify-end mt-2 md:mt-0">
                                                <span className={`px-3 py-1 text-xs font-bold rounded-full uppercase tracking-wide border ${getStatusClasses(rental.status)}`}>
                                                    {rental.status.replace(/([A-Z])/g, ' $1').trim()}
                                                </span>
                                                
                                                {/* Action Buttons */}
                                                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                    {rental.status === 'awaitingPlannerConfirmation' && (
                                                        <button onClick={() => handleOpenDeliveryConfirm(rental)} className="bg-green-50 text-green-700 border border-green-200 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-100 transition shadow-sm">
                                                            Confirm Receipt
                                                        </button>
                                                    )}
                                                    {rental.status === 'delivered' && !rental.isNonReturnable && (
                                                        <button onClick={() => handleUpdateBooking(rental.id, 'awaitingPickup')} className="bg-yellow-50 text-yellow-700 border border-yellow-200 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-yellow-100 transition shadow-sm">
                                                            Request Pickup
                                                        </button>
                                                    )}
                                                    {canLeaveReview(rental) && (
                                                        <button onClick={() => handleOpenReviewModal(rental)} className="bg-purple-50 text-purple-700 border border-purple-200 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-purple-100 transition shadow-sm">
                                                            Leave Review
                                                        </button>
                                                    )}
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 group-hover:text-pink-500 transition-colors ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
                                <div className="w-16 h-16 bg-pink-50 text-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">📅</div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">No rentals yet</h3>
                                <p className="text-gray-500 mb-6 max-w-sm mx-auto">Your upcoming parties will appear here. Start planning your next big event today!</p>
                                <button onClick={() => navigateTo('listings')} className="bg-pink-600 text-white px-6 py-2.5 rounded-full font-semibold hover:bg-pink-700 transition shadow-md shadow-pink-200">
                                    Browse Rentals
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'favorites' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {favorites.length > 0 ? favorites.map(listing => (
                            <ListingCard 
                                key={listing.id} 
                                listing={listing} 
                                vendor={vendors[listing.vendorId]} 
                                navigateTo={navigateTo} 
                                user={user} 
                                isFavorite={true} 
                            />
                        )) : (
                            <div className="text-center py-16 col-span-full bg-white rounded-xl shadow-sm border border-gray-100">
                                <div className="w-16 h-16 bg-pink-50 text-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">❤️</div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">No favorites yet</h3>
                                <p className="text-gray-500 mb-6 max-w-sm mx-auto">Save items you love to your wishlist to easily find them later.</p>
                                <button onClick={() => navigateTo('listings')} className="bg-pink-600 text-white px-6 py-2.5 rounded-full font-semibold hover:bg-pink-700 transition shadow-md shadow-pink-200">Browse Listings</button>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'checklist' && (
                    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                        <Checklist user={user} db={db} navigateTo={navigateTo} />
                    </div>
                )}

                {activeTab === 'budget' && (
                    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                        <BudgetTracker user={user} db={db} />
                    </div>
                )}

                {activeTab === 'weddings' && (
                    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                        <WeddingDashboard user={user} navigateTo={navigateTo} db={db} />
                    </div>
                )}

                {activeTab === 'evites' && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                             <div>
                                <h3 className="text-lg font-bold text-gray-900">Your Invitations</h3>
                                <p className="text-sm text-gray-500">Manage RSVPs and event details.</p>
                             </div>
                             <button onClick={() => navigateTo('evite')} className="bg-pink-600 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-pink-700 shadow-md flex items-center gap-2 transition">
                                 <span>+</span> Create New
                             </button>
                        </div>
                        {evites.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {evites.map(evite => (
                                    <div key={evite.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition group">
                                        <div className="h-32 bg-gray-200 relative">
                                            {evite.generatedImage ? (
                                                <img src={evite.generatedImage} alt={evite.title} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-pink-100 to-purple-100">
                                                    <span className="text-4xl">💌</span>
                                                </div>
                                            )}
                                            <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-10 transition-all"></div>
                                        </div>
                                        <div className="p-5">
                                            <div className="flex justify-between items-start mb-1">
                                                <h4 className="font-bold text-lg text-gray-900 truncate pr-2">{evite.title}</h4>
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider flex-shrink-0 ${evite.status === 'Sent' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                    {evite.status || 'Draft'}
                                                </span>
                                            </div>
                                            <div className="text-gray-500 text-sm flex flex-col gap-1 mb-4">
                                                <span className="flex items-center gap-2"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg> {new Date(evite.date).toLocaleDateString()} at {evite.time}</span>
                                                <span className="flex items-center gap-2 truncate"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg> {evite.location}</span>
                                            </div>
                                            <button onClick={() => navigateTo('evite', { id: evite.id })} className="w-full bg-white text-pink-600 border border-pink-200 px-4 py-2 rounded-lg font-semibold hover:bg-pink-50 transition">
                                                Manage Guests & RSVP
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-100">
                                <div className="w-16 h-16 bg-purple-50 text-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">💌</div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">No invitations yet</h3>
                                <p className="text-gray-500 mb-6 max-w-sm mx-auto">Create beautiful AI-generated invitations for your next party.</p>
                                <button onClick={() => navigateTo('evite')} className="bg-purple-600 text-white px-6 py-2.5 rounded-full font-semibold hover:bg-purple-700 transition shadow-md shadow-purple-200">
                                    Create E-vite
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
